import { useEffect, useState } from 'react'
import { get, isEmpty } from 'lodash'
import { useMutation, useQuery } from 'react-query'
import { useSnackbar } from 'notistack'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@mui/material'

import { participantRoutes, SurveyParticipantsType } from './participantRoutes'
import { ConfirmationDialog, UserAvatar } from 'components'
import { API } from 'core/requests'
import { t } from 'i18n'
import { getUserFullName } from 'utils/user'

type Props = {
  open: boolean
  surveyId: number | string | null | undefined
  surveyType: SurveyParticipantsType
  surveyName?: string
  onClose: () => void
  onSuccess?: () => void
}

export default function SurveyRemoveParticipantsDialog({
  open,
  surveyId,
  surveyType,
  surveyName,
  onClose,
  onSuccess,
}: Props) {
  const { enqueueSnackbar } = useSnackbar()
  const routes = participantRoutes[surveyType]

  const [selectedEmployees, setSelectedEmployees] = useState<any[]>([])
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)

  const { data: participants, isFetching: participantsIsLoading } = useQuery(
    [routes.invited, surveyId],
    () =>
      API.base(routes.invited, {
        method: 'GET',
        pathParams: { id: surveyId },
      }),
    { enabled: open && !!surveyId },
  )

  useEffect(() => {
    if (!open) {
      setSelectedEmployees([])
      setConfirmDialogOpen(false)
    }
  }, [open])

  const removeParticipantsMutation = useMutation(
    (userIds: number[]) =>
      API.base(routes.remove, {
        method: 'DELETE',
        pathParams: { id: surveyId },
        body: { user_ids: userIds },
      }),
    {
      onSuccess: (response: any) => {
        if (response?.success) {
          enqueueSnackbar(
            response.message ??
              t('pulseSurveyOverview.employeesRemoval.removeEmployees'),
            { variant: 'success' },
          )
          setSelectedEmployees([])
          setConfirmDialogOpen(false)
          onSuccess?.()
          onClose()
        } else {
          enqueueSnackbar(
            response?.message ??
              t('pulseSurveyOverview.employeesRemoval.errorMessage'),
            { variant: 'error' },
          )
        }
      },
      onError: () => {
        enqueueSnackbar(
          t('pulseSurveyOverview.employeesRemoval.errorMessage'),
          { variant: 'error' },
        )
      },
    },
  )

  const handleClose = () => {
    onClose()
  }

  return (
    <Dialog
      open={open}
      maxWidth='sm'
      fullWidth
      onClose={handleClose}
      aria-labelledby='remove-participants-dialog-title'
      aria-describedby='remove-participants-dialog-description'
    >
      <DialogTitle id='remove-participants-dialog-title'>
        {t('pulseSurveyOverview.employeesRemoval.removeEmployees')}
      </DialogTitle>
      <DialogContent>
        <Grid item xs={12}>
          <Typography variant='body2' color='text.secondary' paragraph>
            {t('pulseSurveyOverview.employeesRemoval.description', {
              surveyName,
            })}
          </Typography>
          <Typography variant='body2' color='error' paragraph>
            {t('pulseSurveyOverview.employeesRemoval.warning')}
          </Typography>
        </Grid>
        <Autocomplete
          multiple
          id='pulseSurveyParticipants'
          data-cy='pulse_remove_participants_autocomplete'
          value={selectedEmployees}
          onChange={(event: any, newValue: any | null) => {
            setSelectedEmployees(newValue || [])
          }}
          autoHighlight
          loading={participantsIsLoading}
          options={participants || []}
          getOptionLabel={employee => getUserFullName(employee)}
          renderOption={(props, employee: any) => (
            <Box
              component='li'
              sx={{ '& > img': { mr: 3, flexShrink: 0 } }}
              {...props}
            >
              <UserAvatar
                userId={get(employee, 'id')}
                name={get(employee, 'first_name')}
                src={get(employee, 'avatar.url')}
                size={24}
              />
              <Box component='span' ml={1}>
                {getUserFullName(employee)}
              </Box>
            </Box>
          )}
          renderInput={params => (
            <TextField
              {...params}
              label={t('pulseSurveyOverview.employeesRemoval.textFieldLabel')}
              variant='outlined'
              inputProps={{
                ...params.inputProps,
              }}
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          color='primary'
          data-cy='pulse_remove_participants_cancel'
        >
          {t('pulseSurveyOverview.employeesRemoval.cancelButton')}
        </Button>
        <Button
          onClick={() => setConfirmDialogOpen(true)}
          color='primary'
          disabled={
            removeParticipantsMutation.isLoading || isEmpty(selectedEmployees)
          }
          data-cy='pulse_remove_participants_submit'
          autoFocus
        >
          {removeParticipantsMutation.isLoading ? (
            <CircularProgress size={24} />
          ) : (
            t('pulseSurveyOverview.employeesRemoval.removeButton')
          )}
        </Button>
      </DialogActions>
      <ConfirmationDialog
        dialogOpen={confirmDialogOpen}
        setDialogOpen={setConfirmDialogOpen}
        message={t('pulseSurveyOverview.employeesRemoval.confirmMessage', {
          count: selectedEmployees.length,
          surveyName,
        })}
        item={null}
        actionProps={{
          onClick: () => {
            setConfirmDialogOpen(false)
            removeParticipantsMutation.mutate(
              selectedEmployees.map(employee => employee.id),
            )
          },
          isLoading: removeParticipantsMutation.isLoading,
          label: t('pulseSurveyOverview.employeesRemoval.confirmButton'),
          color: 'error',
        }}
      />
    </Dialog>
  )
}
